import { DataTableRoot } from "./DataTableRoot";
import { DataTableTable } from "./DataTableTable";

/**
 * Compound primitive for rendering a paginated, sortable, selectable data
 * table. Composed of two parts:
 *
 * - `DataTable.Root` — outer flex column that owns layout and spacing, hosts
 *   the table, and lets sibling chrome (filters, alerts) sit alongside.
 * - `DataTable.Table` — the bordered table surface itself, including its
 *   pagination footer. Consumes the table slice from `useDataTableState`
 *   and the columns built via `createColumnHelper`.
 *
 * State and column definitions are produced outside the tree:
 * - `useDataTableState` returns a `.table` slice that spreads directly onto
 *   `DataTable.Table`.
 * - `createColumnHelper<TData>()` builds typed column definitions.
 *
 * @example
 * ```tsx
 * const columnHelper = createColumnHelper<Row>();
 * const columns = [columnHelper.accessor("name", { headerName: "Name" })];
 *
 * function MyTable({ rows }: { rows: Row[] }) {
 *   const tableState = useDataTableState({ initialSort });
 *   return (
 *     <DataTable.Root>
 *       <DataTable.Table<Row>
 *         {...tableState.table}
 *         data={rows}
 *         columns={columns}
 *         getRowId={(r) => r.id}
 *         totalRowCount={countQuery.data}
 *         noRowsContent="No rows"
 *       />
 *     </DataTable.Root>
 *   );
 * }
 * ```
 */
export const DataTable = {
  Root: DataTableRoot,
  Table: DataTableTable,
};
